import React, { useContext, useEffect, useState } from 'react';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseCol } from '@app/components/common/BaseCol/BaseCol';
import { BaseRow } from '@app/components/common/BaseRow/BaseRow';
import { BaseButtonsForm } from '@app/components/common/forms/BaseButtonsForm/BaseButtonsForm';
import { BaseInput } from '@app/components/common/inputs/BaseInput/BaseInput';
import { BaseSelect } from '@app/components/common/selects/BaseSelect/BaseSelect';
import { Tooltip, Button, notification, Checkbox, Tabs, Col, Form, Row } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProductRequest } from '@app/models/products/ProductRequest';
import { storeProduct, showProduct, editProduct, updateProduct, createProduct } from '@app/api/products.api';
import StatusBar from '@app/components/statusBar/statusBar';
import { notificationController } from '@app/controllers/notificationController';
import { ProductPricingTable } from '../ProductPricing/ProductPricingTable';
import { useHeader } from '@app/components/header/layouts/HeaderContext';

interface TypeOfForm {
  type: 'edit' | 'create' | 'view';
}

interface ProductsFormProps extends TypeOfForm {
  editable: boolean;
  id?: string;
  showEditIcon?: boolean;
  pageTitle: string;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

const ProductsForm: React.FC<ProductsFormProps> = ({
  editable = false,
  id,
  pageTitle,
  type,
  setCurrentBreadcrumName,
}) => {
  const [form] = BaseButtonsForm.useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const [isLoading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const productId = id ? parseInt(id) : null;
  const [status, setStatus] = useState<string | null>(null);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ label: string; value: number }[]>([]);
  const [typeOptions, setTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [pricingOptions, setPricingOptions] = useState<{ label: string; value: string }[]>([]);

  const setApiData = (res: any) => {
    form.setFieldsValue({
      product_category_id: res?.product?.product_category_id,
      code: res?.product?.code,
      name: res?.product?.name,
      description: res?.product?.description,
      type: res.product.type,
      pricing_strategy: res.product.pricing_strategy,
      status: res?.product?.status,
      exportable: res?.product?.exportable ?? 'false',
      importable: res?.product?.importable ?? 'false',
    });
    setStatus(res.product.status);
    setCurrentBreadcrumName(res?.product?.code || 'New');
  };

  const setCategories = (res: any) => {
    const mappedCategories = res.product_categories.map((categories: any) => ({
      label: categories.name,
      value: categories.id,
    }));
    setCategoryOptions(mappedCategories);
  };

  const setOptions = (res: any) => {
    const mappedStatuses = res.status_options.map((status: any) => ({
      label: status.label,
      value: status.value,
    }));
    setStatusOptions(mappedStatuses);
  };

  const setTypes = (res: any) => {
    const mappedTypes = res.types.map((type: any) => ({
      label: type.label,
      value: type.value,
    }));
    setTypeOptions(mappedTypes);
  };

  const setPricing = (res: any) => {
    const mappedPricing = res.pricing_strategy.map((pricing: any) => ({
      label: pricing.label,
      value: pricing.value,
    }));
    setPricingOptions(mappedPricing);
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={isLoading} onClick={() => form.submit()} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/products')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-product`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, isLoading, editable, id, addHeaderButton, t, navigate, removeHeaderButton]);

  useEffect(() => {
    setLoading(true);
    if (productId) {
      if (type === 'view') {
        showProduct(productId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setCategories(res);
            setTypes(res);
            setPricing(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to category', description: err.message });
            console.error(err);
          });
      } else if (type === 'edit') {
        editProduct(productId)
          .then((res: any) => {
            setApiData(res);
            setOptions(res);
            setCategories(res);
            setTypes(res);
            setPricing(res);
            setLoading(false);
          })
          .catch((err: any) => {
            setLoading(false);
            notification.error({ message: 'Failed to edit category', description: err.message });
            console.error(err);
          });
      }
    } else if (type === 'create') {
      createProduct()
        .then((res: any) => {
          setOptions(res);
          setCategories(res);
          setTypes(res);
          setPricing(res);
          setLoading(false);
          setLoading(false);
        })
        .catch((err: any) => {
          setLoading(false);
          notification.error({ message: 'Failed to fetch categories', description: err.message });
          console.error(err);
        });
    }
  }, [id, type, setCurrentBreadcrumName]);

  const handleSubmit = (values: ProductRequest) => {
    if (editable) {
      const submissionData = {
        ...values,
        importable: values.importable ?? false,
        exportable: values.exportable ?? false,
      };

      if (type === 'create') {
        storeProduct(submissionData)
          .then((res: any) => {
            setLoading(false);
            navigate(`/view-product/${res?.data?.id}`);
          })
          .catch((err: any) => {
            notificationController.error({ message: err.message });
            setLoading(false);
          });
      } else if (type === 'edit' && productId) {
        updateProduct(productId, submissionData)
          .then(() => {
            setLoading(false);
            navigate(`/view-product/${id}`);
          })
          .catch((err: any) => {
            notificationController.error({ message: `${err.message}` });
            setLoading(false);
          });
      }
    }
    console.log('Form Submitted');
  };

  return (
    <>
      <BaseRow gutter={[16, 16]}>
        <BaseCol span={24}>
          <StatusBar id={String(productId)} status={status} />
        </BaseCol>
        <BaseCol span={24}>
          <BaseCard
            id="productsForm"
            title={pageTitle}
            padding="1.25rem"
            // extra={
            //   type === 'view' && (
            //     <Tooltip title="Edit">
            //       <Button type="ghost" onClick={() => navigate(`/edit-product`, { state: { id } })}>
            //         Edit
            //       </Button>
            //     </Tooltip>
            //   )
            // }
          >
            <BaseRow gutter={[16, 16]}>
              <BaseCol span={24}>
                <Form
                  form={form}
                  name="productsForm"
                  onFinish={handleSubmit}
                  //  isFieldsChanged={false}
                  labelCol={{ lg: { span: 6 }, md: { span: 6 }, sm: { span: 24 } }}
                  labelAlign="left"
                  labelWrap
                  wrapperCol={{ lg: { span: 16 }, md: { span: 18 }, sm: { span: 24 } }}
                  size="small"
                  style={{ marginLeft: '40px' }}
                >
                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item
                        name="code"
                        label="Item Code"
                        rules={[{ required: true, message: 'Item Code is required' }]}
                      >
                        <BaseInput placeholder="Enter Item Code" readOnly={!editable} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item label="Select Category" name="product_category_id">
                        {!editable ? (
                          <div
                            style={{
                              fontWeight: 'normal',
                            }}
                          >
                            {categoryOptions.find(
                              (option) => option.value === form.getFieldValue('product_category_id'),
                            )?.label || '-'}
                          </div>
                        ) : (
                          <BaseSelect
                            placeholder="Select Category"
                            disabled={!editable}
                            options={categoryOptions}
                            value={form.getFieldValue('product_category_id')}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
                        <BaseInput placeholder="Enter Name" readOnly={!editable} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={24} lg={12}>
                      <Form.Item label="Type" name="type">
                        {!editable ? (
                          <BaseInput
                            readOnly
                            value={
                              typeOptions.find((option) => option.value === form.getFieldValue('type'))?.label || ''
                            }
                          />
                        ) : (
                          <BaseSelect
                            placeholder="Select Type"
                            options={typeOptions}
                            value={form.getFieldValue('type')}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item label="Status" name="status">
                        {!editable ? (
                          <BaseInput
                            readOnly
                            value={
                              statusOptions.find((option) => option.value === form.getFieldValue('status'))?.label || ''
                            }
                          />
                        ) : (
                          <BaseSelect
                            placeholder="Select Status"
                            options={statusOptions}
                            value={form.getFieldValue('status')}
                          />
                        )}
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item label="Pricing Strategy" name="pricing_strategy">
                        {!editable ? (
                          <BaseInput
                            readOnly
                            value={
                              pricingOptions.find((option) => option.value === form.getFieldValue('pricing_strategy'))
                                ?.label || ''
                            }
                          />
                        ) : (
                          <BaseSelect
                            placeholder="Select Pricing Method"
                            options={pricingOptions}
                            value={form.getFieldValue('pricing_strategy')}
                          />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item label="Description" name="description">
                        {!editable ? (
                          <BaseInput readOnly value={form.getFieldValue('description') || ''} />
                        ) : (
                          <BaseInput.TextArea placeholder="Enter Description" readOnly={!editable} />
                        )}
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item name="importable" valuePropName="checked">
                        <Checkbox disabled={!editable}> Importable </Checkbox>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={24} lg={12}>
                      <Form.Item name="exportable" valuePropName="checked">
                        <Checkbox disabled={!editable}> Exportable </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </BaseCol>
              {type != 'create' && (
                <BaseCol span={24}>
                  <Tabs>
                    <Tabs.TabPane tab="Pricing" key="tab-1">
                      <ProductPricingTable
                        product_category_id={null}
                        product_id={productId}
                        product_variety_id={null}
                      />
                    </Tabs.TabPane>
                  </Tabs>
                </BaseCol>
              )}
            </BaseRow>
          </BaseCard>
        </BaseCol>
      </BaseRow>
    </>
  );
};

export default ProductsForm;
